import Tracking from '@vromo/analytics';
import { useStore } from '@vromo/mobx-react';
import { Vehicle } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import Input from '@vromo/ui/components/Input';
import { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Select2 from '@vromo/ui/components/Select2';
import Title from '@vromo/ui/components/Title';
import { useToasts } from '@vromo/ui/components/Toasts';
import sortBy from 'lodash/sortBy';
import { observer } from 'mobx-react-lite';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { update } from 'serializr';
import { qaLabelTeams } from '../../../../../common-qa-labels/teams';
import { RootStore } from '../../stores/RootStore';

interface Props {
    close: () => void;
}

type NumberOption = {
    label: string;
    value: number;
};

const MemoInput = memo(({ ...props }: any) => (
    <fieldset>
        <Input type="text" {...props} />
    </fieldset>
));

const MemoDropdown = memo(({ label, ...props }: any) => (
    <fieldset>
        <label data-testid={qaLabelTeams.newDriverModal.groupLabel}>{label}</label>
        <Select2 {...props} />
    </fieldset>
));

const NewDriverForm = observer<Props>(({ close }) => {
    const {
        groupStore,
        vehicleStore,
        authStore: { role, graph }
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();
    const { t } = useTranslation();

    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [group, setGroup] = useState<number | null>(role.group!.id);
    const [external, setExternal] = useState<string>('');
    const [isLoading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        Tracking.track('Opened New Driver Form');
    }, []);

    const groupOptions = useMemo(
        () =>
            sortBy(
                groupStore.groups.map((g) => ({ label: g.name, value: g.id })),
                ['label']
            ),
        [groupStore.groups]
    );

    const dropdownValue = useMemo(
        () => groupOptions.find((g) => g.value === group) || null,
        [group]
    );

    const isValidDriver = () => {
        const allFieldsFilled = email.length !== 0 && name.length !== 0 && group !== null;

        const validEmail = /@.+/.test(email);

        const driverExistsInGroup = vehicleStore
            .withGroup(group!)
            .some((v: Vehicle) => v.driver?.contact?.email === email);

        const showError = (validated: boolean, message: string) => {
            if (validated) return true;
            showToast(message);
            return false;
        };

        return (
            showError(allFieldsFilled, t('teams.allFieldsFilledDriver')) &&
            showError(validEmail, t('teams.invalidEmail')) &&
            showError(!driverExistsInGroup, t('teams.driverAlreadyExists'))
        );
    };

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        if (!group) {
            showToast(t('teams.noGroupSelected'), 'error');
            return;
        }

        if (!isValidDriver()) return;

        const requestObject = [
            {
                name,
                group,
                external,
                driver: { contact: { email } }
            }
        ];

        setLoading(true);
        graph.root
            .graph()
            .roles(role.id)
            .vehicles()
            .post(requestObject)
            .then(({ items }) => {
                Tracking.track(t('teams.driverCreated'));

                update(vehicleStore.vehicles, { items, type: 'create' });

                setLoading(false);
                close();
            })
            .catch((err) => {
                console.error(err);
                showToast(t('teams.errorCreatingDriver'));
                setLoading(false);
            });
    };

    const updateName = useCallback((e: any) => setName(e.target.value), []);
    const handleEmailChange = useCallback((e: any) => setEmail(e.target.value), []);
    const updateExternalId = useCallback((e: any) => setExternal(e.target.value), []);

    const onChangeGroup = useCallback((selected: NumberOption) => {
        setGroup(selected?.value);
    }, []);

    return (
        <>
            <ModalHeader>
                <Title data-testid={qaLabelTeams.newDriverModal.title}>
                    {t('teams.addNewDriver')}
                </Title>
            </ModalHeader>
            <ModalContent style={{ overflow: 'unset' }}>
                <MemoInput
                    label={t('teams.addNewDriverEmail')}
                    value={email}
                    onChange={handleEmailChange}
                    data-testid={qaLabelTeams.newDriverModal.emailInput}
                />
                <MemoInput
                    label={t('teams.drivingForLabel')}
                    tooltip={t('teams.drivingForTooltip')}
                    value={name}
                    onChange={updateName}
                    placeholder={t('teams.drivingForPlaceholder')}
                    data-testid={qaLabelTeams.newDriverModal.drivingForInput}
                />
                <MemoDropdown
                    label={t('teams.driverGroup')}
                    data-testid={qaLabelTeams.newDriverModal.groupDropdown}
                    onChange={onChangeGroup}
                    value={dropdownValue}
                    options={groupOptions}
                    placeholder={t('teams.selectGroup')}
                    isClearable
                />
                <MemoInput
                    label={t('teams.extIdentifier')}
                    tooltip={t('teams.extIdentifierTooltip')}
                    optional
                    value={external}
                    onChange={updateExternalId}
                    placeholder={t('teams.extIdentifierPlaceholder')}
                    data-testid={qaLabelTeams.newDriverModal.externalIdentifierInput}
                />
            </ModalContent>
            <ModalActions>
                <Button onClick={close}>{t('teams.cancel')}</Button>
                <Button
                    primary
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleSubmit}
                    data-testid={qaLabelTeams.newDriverModal.addDriverButton}>
                    {t('teams.addDriver')}
                </Button>
            </ModalActions>
        </>
    );
});

export default NewDriverForm;
